// @ts-nocheck
/**
 * カードコレクションシステム - パック開封
 *
 * パックの開封ロジックを管理
 */

import { ECONOMY, ATTRIBUTE_PACK, ROYAL_PACK } from '../data/constants';
import { raritySystem } from './raritySystem';
import { collectionManager } from './collectionManager';
import { currencyManager } from './currencyManager';
import { valueCalculator } from './valueCalculator';

// ========================================
// パック開封
// ========================================

/**
 * パックを開封（5枚抽選）
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} cardValueMap - カードID -> 価値情報のマップ
 * @returns {Array<{cardId: string, rarity: string, card: Object}>}
 */
export const openPack = (allCards, cardValueMap) => {
  const results = [];

  // 枠1-2: C確定
  results.push(drawCard(allCards, cardValueMap, 'C_ONLY'));
  results.push(drawCard(allCards, cardValueMap, 'C_ONLY'));

  // 枠3-4: C/UC（UC 30%）
  results.push(drawCard(allCards, cardValueMap, 'C_UC'));
  results.push(drawCard(allCards, cardValueMap, 'C_UC'));

  // 枠5: R以上確定
  results.push(drawCard(allCards, cardValueMap, 'R_PLUS'));

  return results;
};

/**
 * 単一カードを抽選
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} cardValueMap - カードID -> 価値情報のマップ
 * @param {string} slotType - 'C_ONLY' | 'C_UC' | 'R_PLUS'
 * @returns {{cardId: string, rarity: string, card: Object}}
 */
const drawCard = (allCards, cardValueMap, slotType) => {
  // 1. レアリティを決定
  const rarity = raritySystem.drawRarity(slotType);

  // 2. そのレアリティで出現可能なカードを絞り込み
  const eligibleCards = allCards.filter(card => {
    const valueInfo = cardValueMap.get(card.id);
    if (!valueInfo) return true; // 価値情報がない場合は許可

    const tier = valueInfo.tier || 'D';

    // GRは禁忌カードのみ
    if (rarity === 'GR') {
      return card.isForbidden || (Array.isArray(card.keyword) && card.keyword.includes('禁忌カード'));
    }

    return raritySystem.canAppearAtRarity(tier, rarity);
  });

  // カードがない場合のフォールバック
  if (eligibleCards.length === 0) {
    // 全カードから選択（通常は起きない）
    const fallbackCard = allCards[Math.floor(Math.random() * allCards.length)];
    return {
      cardId: fallbackCard.id,
      rarity: 'C', // Cにフォールバック
      card: fallbackCard,
    };
  }

  // 3. ランダムに1枚選択
  const card = eligibleCards[Math.floor(Math.random() * eligibleCards.length)];

  return {
    cardId: card.id,
    rarity,
    card,
  };
};

// ========================================
// パック購入・開封統合
// ========================================

/**
 * パックを購入して開封
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} [cardValueMap] - カードID -> 価値情報のマップ（省略時は計算）
 * @returns {Object} { success, error?, cards?, playerData? }
 */
export const buyAndOpenPack = (playerData, allCards, cardValueMap = null) => {
  // ゴールドチェック
  if (!currencyManager.canBuyPack(playerData)) {
    return {
      success: false,
      error: 'ゴールドが足りません',
    };
  }

  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  // ゴールドを消費
  let newData = currencyManager.payForPack(playerData);
  if (!newData) {
    return {
      success: false,
      error: 'ゴールドの消費に失敗しました',
    };
  }

  // パック開封
  const cards = openPack(allCards, valueMap);

  // 各カードをコレクションに追加
  for (const { cardId, rarity } of cards) {
    newData = collectionManager.addCard(newData, cardId, rarity);
  }

  return {
    success: true,
    cards,
    playerData: newData,
  };
};

/**
 * 無料パックを開封（勝利報酬等）
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} [cardValueMap] - カードID -> 価値情報のマップ
 * @returns {Object} { cards, playerData }
 */
export const openFreePack = (playerData, allCards, cardValueMap = null) => {
  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  // パック開封
  const cards = openPack(allCards, valueMap);

  // 各カードをコレクションに追加
  let newData = {
    ...playerData,
    stats: {
      ...playerData.stats,
      packsOpened: (playerData.stats.packsOpened || 0) + 1,
    },
  };

  for (const { cardId, rarity } of cards) {
    newData = collectionManager.addCard(newData, cardId, rarity);
  }

  return {
    cards,
    playerData: newData,
  };
};

/**
 * 未開封パックを1つ開封
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} [cardValueMap] - カードID -> 価値情報のマップ
 * @returns {Object} { success, error?, cards?, playerData? }
 */
export const openUnopenedPack = (playerData, allCards, cardValueMap = null) => {
  // 未開封パックの確認
  if (!playerData.unopenedPacks || playerData.unopenedPacks <= 0) {
    return {
      success: false,
      error: '未開封パックがありません',
    };
  }

  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  // パック開封
  const cards = openPack(allCards, valueMap);

  // 各カードをコレクションに追加 & 未開封パック数を減らす
  let newData = {
    ...playerData,
    unopenedPacks: playerData.unopenedPacks - 1,
    stats: {
      ...playerData.stats,
      packsOpened: (playerData.stats.packsOpened || 0) + 1,
    },
  };

  for (const { cardId, rarity } of cards) {
    newData = collectionManager.addCard(newData, cardId, rarity);
  }

  return {
    success: true,
    cards,
    playerData: newData,
  };
};

// ========================================
// パック情報
// ========================================

/**
 * パック情報を取得
 * @returns {Object}
 */
export const getPackInfo = () => {
  return {
    price: ECONOMY.PACK_PRICE,
    cardsPerPack: ECONOMY.CARDS_PER_PACK,
    priceFormatted: currencyManager.formatGold(ECONOMY.PACK_PRICE),
  };
};

// ========================================
// まとめ買い・まとめ開封
// ========================================

/**
 * 複数パックを購入して開封
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object[]} allCards - 全カードデータ
 * @param {number} packCount - 購入パック数
 * @param {Map} [cardValueMap] - カードID -> 価値情報のマップ（省略時は計算）
 * @returns {Object} { success, error?, cards?, playerData?, packCount? }
 */
export const buyAndOpenMultiplePacks = (playerData, allCards, packCount, cardValueMap = null) => {
  const totalCost = ECONOMY.PACK_PRICE * packCount;

  // ゴールドチェック
  if (!currencyManager.canAfford(playerData, totalCost)) {
    return {
      success: false,
      error: 'ゴールドが足りません',
    };
  }

  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  // ゴールドを消費
  let newData = currencyManager.spendGold(playerData, totalCost, 'pack_multi');
  if (!newData) {
    return {
      success: false,
      error: 'ゴールドの消費に失敗しました',
    };
  }

  // パック開封統計を更新
  newData = {
    ...newData,
    stats: {
      ...newData.stats,
      packsOpened: (newData.stats.packsOpened || 0) + packCount,
    },
  };

  // 全パックを開封してカードを統合
  const allOpenedCards = [];
  for (let i = 0; i < packCount; i++) {
    const packCards = openPack(allCards, valueMap);
    allOpenedCards.push(...packCards);
  }

  // 各カードをコレクションに追加
  for (const { cardId, rarity } of allOpenedCards) {
    newData = collectionManager.addCard(newData, cardId, rarity);
  }

  return {
    success: true,
    cards: allOpenedCards,
    playerData: newData,
    packCount,
  };
};

/**
 * 複数の未開封パックを開封
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object[]} allCards - 全カードデータ
 * @param {number} packCount - 開封パック数
 * @param {Map} [cardValueMap] - カードID -> 価値情報のマップ
 * @returns {Object} { success, error?, cards?, playerData?, packCount? }
 */
export const openMultipleUnopenedPacks = (playerData, allCards, packCount, cardValueMap = null) => {
  // 未開封パックの確認
  const availablePacks = playerData.unopenedPacks || 0;
  if (availablePacks < packCount) {
    return {
      success: false,
      error: `未開封パックが足りません（${availablePacks}個しかありません）`,
    };
  }

  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  // 全パックを開封してカードを統合
  const allOpenedCards = [];
  for (let i = 0; i < packCount; i++) {
    const packCards = openPack(allCards, valueMap);
    allOpenedCards.push(...packCards);
  }

  // 各カードをコレクションに追加 & 未開封パック数を減らす
  let newData = {
    ...playerData,
    unopenedPacks: playerData.unopenedPacks - packCount,
    stats: {
      ...playerData.stats,
      packsOpened: (playerData.stats.packsOpened || 0) + packCount,
    },
  };

  for (const { cardId, rarity } of allOpenedCards) {
    newData = collectionManager.addCard(newData, cardId, rarity);
  }

  return {
    success: true,
    cards: allOpenedCards,
    playerData: newData,
    packCount,
  };
};

/**
 * まとめ買いオプションを取得
 * @returns {Array<{count: number, totalPrice: number, discountRate: number}>}
 */
export const getBulkBuyOptions = () => {
  return [
    { count: 3, totalPrice: ECONOMY.PACK_PRICE * 3, discountRate: 0 },
    { count: 5, totalPrice: ECONOMY.PACK_PRICE * 5, discountRate: 0 },
    { count: 10, totalPrice: ECONOMY.PACK_PRICE * 10, discountRate: 0 },
  ];
};

// ========================================
// 属性別パック
// ========================================

/**
 * 属性フィルター付きでカードを抽選
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} cardValueMap - カードID -> 価値情報のマップ
 * @param {string} slotType - 'C_ONLY' | 'C_UC' | 'R_PLUS'
 * @param {string} attribute - フィルターする属性
 * @returns {{cardId: string, rarity: string, card: Object}}
 */
const drawCardFromAttribute = (allCards, cardValueMap, slotType, attribute) => {
  // 属性でフィルタリング
  const attributeCards = allCards.filter(card => card.attribute === attribute);

  // 1. レアリティを決定
  const rarity = raritySystem.drawRarity(slotType);

  // 2. そのレアリティで出現可能なカードを絞り込み
  const eligibleCards = attributeCards.filter(card => {
    const valueInfo = cardValueMap.get(card.id);
    if (!valueInfo) return true;

    const tier = valueInfo.tier || 'D';

    // GRは禁忌カードのみ
    if (rarity === 'GR') {
      return card.isForbidden || (Array.isArray(card.keyword) && card.keyword.includes('禁忌カード'));
    }

    return raritySystem.canAppearAtRarity(tier, rarity);
  });

  // カードがない場合のフォールバック
  if (eligibleCards.length === 0) {
    // 属性内全カードから選択
    if (attributeCards.length > 0) {
      const fallbackCard = attributeCards[Math.floor(Math.random() * attributeCards.length)];
      return {
        cardId: fallbackCard.id,
        rarity: 'C',
        card: fallbackCard,
      };
    }
    // 属性カードがない場合は全カードからフォールバック
    const fallbackCard = allCards[Math.floor(Math.random() * allCards.length)];
    return {
      cardId: fallbackCard.id,
      rarity: 'C',
      card: fallbackCard,
    };
  }

  // 3. ランダムに1枚選択
  const card = eligibleCards[Math.floor(Math.random() * eligibleCards.length)];

  return {
    cardId: card.id,
    rarity,
    card,
  };
};

/**
 * 属性別パックを開封（5枚抽選）
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} cardValueMap - カードID -> 価値情報のマップ
 * @param {string} attribute - 対象属性
 * @returns {Array<{cardId: string, rarity: string, card: Object}>}
 */
export const openAttributePack = (allCards, cardValueMap, attribute) => {
  const results = [];

  // 枠1-2: C確定
  results.push(drawCardFromAttribute(allCards, cardValueMap, 'C_ONLY', attribute));
  results.push(drawCardFromAttribute(allCards, cardValueMap, 'C_ONLY', attribute));

  // 枠3-4: C/UC（UC 30%）
  results.push(drawCardFromAttribute(allCards, cardValueMap, 'C_UC', attribute));
  results.push(drawCardFromAttribute(allCards, cardValueMap, 'C_UC', attribute));

  // 枠5: R以上確定
  results.push(drawCardFromAttribute(allCards, cardValueMap, 'R_PLUS', attribute));

  return results;
};

/**
 * 属性別パックを購入して開封
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object[]} allCards - 全カードデータ
 * @param {string} attribute - 対象属性
 * @param {Map} [cardValueMap] - カードID -> 価値情報のマップ
 * @returns {Object} { success, error?, cards?, playerData? }
 */
export const buyAndOpenAttributePack = (playerData, allCards, attribute, cardValueMap = null) => {
  const price = ATTRIBUTE_PACK.SHOP_PRICE;

  // ゴールドチェック
  if (!currencyManager.canAfford(playerData, price)) {
    return {
      success: false,
      error: 'ゴールドが足りません',
    };
  }

  // 属性チェック
  if (!ATTRIBUTE_PACK.ATTRIBUTES.includes(attribute)) {
    return {
      success: false,
      error: '無効な属性です',
    };
  }

  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  // ゴールドを消費
  let newData = currencyManager.spendGold(playerData, price, `attribute_pack_${attribute}`);
  if (!newData) {
    return {
      success: false,
      error: 'ゴールドの消費に失敗しました',
    };
  }

  // パック開封統計を更新
  newData = {
    ...newData,
    stats: {
      ...newData.stats,
      packsOpened: (newData.stats.packsOpened || 0) + 1,
    },
  };

  // パック開封
  const cards = openAttributePack(allCards, valueMap, attribute);

  // 各カードをコレクションに追加
  for (const { cardId, rarity } of cards) {
    newData = collectionManager.addCard(newData, cardId, rarity);
  }

  return {
    success: true,
    cards,
    playerData: newData,
    packType: 'attribute',
    attribute,
  };
};

/**
 * 属性別パック情報を取得
 * @returns {Object}
 */
export const getAttributePackInfo = () => {
  return {
    price: ATTRIBUTE_PACK.SHOP_PRICE,
    merchantPrice: ATTRIBUTE_PACK.MERCHANT_PRICE,
    cardsPerPack: ATTRIBUTE_PACK.CARDS_PER_PACK,
    attributes: ATTRIBUTE_PACK.ATTRIBUTES,
    priceFormatted: currencyManager.formatGold(ATTRIBUTE_PACK.SHOP_PRICE),
  };
};

// ========================================
// ロイヤルパック
// ========================================

/**
 * ロイヤルパック用のレアリティ抽選
 * @param {string} slotType - 'SR_PLUS' | 'UR_PLUS' | 'HR_PLUS'
 * @returns {string} レアリティ
 */
const drawRoyalRarity = (slotType) => {
  const rand = Math.random();
  let rates;

  switch (slotType) {
    case 'SR_PLUS':
      rates = ROYAL_PACK.SLOT_1_2_RATES;
      break;
    case 'UR_PLUS':
      rates = ROYAL_PACK.SLOT_3_4_RATES;
      break;
    case 'HR_PLUS':
      rates = ROYAL_PACK.SLOT_5_RATES;
      break;
    default:
      return 'SR';
  }

  let cumulative = 0;
  for (const [rarity, rate] of Object.entries(rates)) {
    cumulative += rate;
    if (rand < cumulative) {
      return rarity;
    }
  }

  // フォールバック（最初のレアリティ）
  return Object.keys(rates)[0];
};

/**
 * ロイヤルパック用のカード抽選
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} cardValueMap - カードID -> 価値情報のマップ
 * @param {string} slotType - 'SR_PLUS' | 'UR_PLUS' | 'HR_PLUS'
 * @returns {{cardId: string, rarity: string, card: Object}}
 */
const drawCardForRoyal = (allCards, cardValueMap, slotType) => {
  // 1. レアリティを決定
  const rarity = drawRoyalRarity(slotType);

  // 2. そのレアリティで出現可能なカードを絞り込み
  const eligibleCards = allCards.filter(card => {
    const valueInfo = cardValueMap.get(card.id);
    if (!valueInfo) return true;

    const tier = valueInfo.tier || 'D';

    // GRは禁忌カードのみ
    if (rarity === 'GR') {
      return card.isForbidden || (Array.isArray(card.keyword) && card.keyword.includes('禁忌カード'));
    }

    return raritySystem.canAppearAtRarity(tier, rarity);
  });

  // カードがない場合のフォールバック
  if (eligibleCards.length === 0) {
    const fallbackCard = allCards[Math.floor(Math.random() * allCards.length)];
    return {
      cardId: fallbackCard.id,
      rarity: 'SR', // SRにフォールバック（ロイヤルなので）
      card: fallbackCard,
    };
  }

  // 3. ランダムに1枚選択
  const card = eligibleCards[Math.floor(Math.random() * eligibleCards.length)];

  return {
    cardId: card.id,
    rarity,
    card,
  };
};

/**
 * ロイヤルパックを開封（5枚抽選）
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} cardValueMap - カードID -> 価値情報のマップ
 * @returns {Array<{cardId: string, rarity: string, card: Object}>}
 */
export const openRoyalPack = (allCards, cardValueMap) => {
  const results = [];

  // 枠1-2: SR以上確定
  results.push(drawCardForRoyal(allCards, cardValueMap, 'SR_PLUS'));
  results.push(drawCardForRoyal(allCards, cardValueMap, 'SR_PLUS'));

  // 枠3-4: UR以上確定
  results.push(drawCardForRoyal(allCards, cardValueMap, 'UR_PLUS'));
  results.push(drawCardForRoyal(allCards, cardValueMap, 'UR_PLUS'));

  // 枠5: HR以上確定
  results.push(drawCardForRoyal(allCards, cardValueMap, 'HR_PLUS'));

  return results;
};

/**
 * ロイヤルパックを購入して開封
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object[]} allCards - 全カードデータ
 * @param {Map} [cardValueMap] - カードID -> 価値情報のマップ
 * @returns {Object} { success, error?, cards?, playerData? }
 */
export const buyAndOpenRoyalPack = (playerData, allCards, cardValueMap = null) => {
  const price = ROYAL_PACK.PRICE;

  // ゴールドチェック
  if (!currencyManager.canAfford(playerData, price)) {
    return {
      success: false,
      error: 'ゴールドが足りません（100,000G必要）',
    };
  }

  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  // ゴールドを消費
  let newData = currencyManager.spendGold(playerData, price, 'royal_pack');
  if (!newData) {
    return {
      success: false,
      error: 'ゴールドの消費に失敗しました',
    };
  }

  // パック開封統計を更新
  newData = {
    ...newData,
    stats: {
      ...newData.stats,
      packsOpened: (newData.stats.packsOpened || 0) + 1,
      royalPacksOpened: (newData.stats.royalPacksOpened || 0) + 1,
    },
  };

  // パック開封
  const cards = openRoyalPack(allCards, valueMap);

  // 各カードをコレクションに追加
  for (const { cardId, rarity } of cards) {
    newData = collectionManager.addCard(newData, cardId, rarity);
  }

  return {
    success: true,
    cards,
    playerData: newData,
    packType: 'royal',
  };
};

/**
 * ロイヤルパック情報を取得
 * @returns {Object}
 */
export const getRoyalPackInfo = () => {
  return {
    price: ROYAL_PACK.PRICE,
    cardsPerPack: ROYAL_PACK.CARDS_PER_PACK,
    priceFormatted: currencyManager.formatGold(ROYAL_PACK.PRICE),
    slot1_2: 'SR以上確定',
    slot3_4: 'UR以上確定',
    slot5: 'HR以上確定',
  };
};

/**
 * 複数ロイヤルパックを購入して開封
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object[]} allCards - 全カードデータ
 * @param {number} packCount - 購入パック数
 * @param {Map} [cardValueMap] - カードID -> 価値情報のマップ（省略時は計算）
 * @returns {Object} { success, error?, cards?, playerData?, packCount? }
 */
export const buyAndOpenMultipleRoyalPacks = (playerData, allCards, packCount, cardValueMap = null) => {
  const totalCost = ROYAL_PACK.PRICE * packCount;

  // ゴールドチェック
  if (!currencyManager.canAfford(playerData, totalCost)) {
    return {
      success: false,
      error: `ゴールドが足りません（${currencyManager.formatGold(totalCost)}必要）`,
    };
  }

  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  // ゴールドを消費
  let newData = currencyManager.spendGold(playerData, totalCost, 'royal_pack_multi');
  if (!newData) {
    return {
      success: false,
      error: 'ゴールドの消費に失敗しました',
    };
  }

  // パック開封統計を更新
  newData = {
    ...newData,
    stats: {
      ...newData.stats,
      packsOpened: (newData.stats.packsOpened || 0) + packCount,
      royalPacksOpened: (newData.stats.royalPacksOpened || 0) + packCount,
    },
  };

  // 全パックを開封してカードを統合
  const allOpenedCards = [];
  for (let i = 0; i < packCount; i++) {
    const packCards = openRoyalPack(allCards, valueMap);
    allOpenedCards.push(...packCards);
  }

  // 各カードをコレクションに追加
  for (const { cardId, rarity } of allOpenedCards) {
    newData = collectionManager.addCard(newData, cardId, rarity);
  }

  return {
    success: true,
    cards: allOpenedCards,
    playerData: newData,
    packCount,
    packType: 'royal',
  };
};

/**
 * ロイヤルパックのまとめ買いオプションを取得
 * @returns {Array<{count: number, totalPrice: number}>}
 */
export const getRoyalBulkBuyOptions = () => {
  return [
    { count: 3, totalPrice: ROYAL_PACK.PRICE * 3 },
    { count: 5, totalPrice: ROYAL_PACK.PRICE * 5 },
  ];
};

// ========================================
// タイプ別未開封パック（報酬用）
// ========================================

/**
 * パックタイプ名を取得
 * @param {string} packType - パックタイプ
 * @returns {string} 表示名
 */
export const getPackTypeName = (packType) => {
  const names = {
    normal: 'ノーマルパック',
    fire: '炎パック',
    water: '水パック',
    light: '光パック',
    dark: '闇パック',
    primitive: '原始パック',
    future: '未来パック',
    royal: 'ロイヤルパック',
  };
  return names[packType] || packType;
};

/**
 * パックタイプのアイコンを取得
 * @param {string} packType - パックタイプ
 * @returns {string} アイコン
 */
export const getPackTypeIcon = (packType) => {
  const icons = {
    normal: '🎴',
    fire: '🔥',
    water: '💧',
    light: '✨',
    dark: '🌙',
    primitive: '🌿',
    future: '⚡',
    royal: '👑',
  };
  return icons[packType] || '📦';
};

/**
 * タイプ別未開封パックの数量を取得
 * @param {Object} playerData - プレイヤーデータ
 * @returns {Object} { [packType]: count }
 */
export const getTypedUnopenedPackCounts = (playerData) => {
  const defaultCounts = {
    normal: 0,
    fire: 0,
    water: 0,
    light: 0,
    dark: 0,
    primitive: 0,
    future: 0,
    royal: 0,
  };

  if (!playerData.unopenedPacksByType) {
    return defaultCounts;
  }

  return {
    ...defaultCounts,
    ...playerData.unopenedPacksByType,
  };
};

/**
 * タイプ別未開封パックを1つ開封
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object[]} allCards - 全カードデータ
 * @param {string} packType - パックタイプ ('fire', 'water', etc.)
 * @param {Map} [cardValueMap] - カードID -> 価値情報のマップ
 * @returns {Object} { success, error?, cards?, playerData?, packType? }
 */
export const openTypedUnopenedPack = (playerData, allCards, packType, cardValueMap = null) => {
  // 未開封パックの確認
  const counts = getTypedUnopenedPackCounts(playerData);
  if (counts[packType] <= 0) {
    return {
      success: false,
      error: `${getPackTypeName(packType)}がありません`,
    };
  }

  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  // パックタイプに応じた開封処理
  let cards;
  if (packType === 'normal') {
    cards = openPack(allCards, valueMap);
  } else if (packType === 'royal') {
    cards = openRoyalPack(allCards, valueMap);
  } else {
    // 属性パック（fire, water, light, dark, primitive, future）
    const attribute = {
      fire: '炎',
      water: '水',
      light: '光',
      dark: '闇',
      primitive: '原始',
      future: '未来',
    }[packType];

    if (!attribute) {
      return {
        success: false,
        error: '無効なパックタイプです',
      };
    }

    cards = openAttributePack(allCards, valueMap, attribute);
  }

  // 各カードをコレクションに追加 & 未開封パック数を減らす
  let newData = {
    ...playerData,
    unopenedPacksByType: {
      ...playerData.unopenedPacksByType,
      [packType]: (playerData.unopenedPacksByType?.[packType] || 0) - 1,
    },
    stats: {
      ...playerData.stats,
      packsOpened: (playerData.stats.packsOpened || 0) + 1,
    },
  };

  // ノーマルパックの場合、互換性のためunopenedPacksも更新
  if (packType === 'normal') {
    newData.unopenedPacks = Math.max(0, (playerData.unopenedPacks || 0) - 1);
  }

  for (const { cardId, rarity } of cards) {
    newData = collectionManager.addCard(newData, cardId, rarity);
  }

  return {
    success: true,
    cards,
    playerData: newData,
    packType,
  };
};

/**
 * 複数のタイプ別未開封パックを開封
 * @param {Object} playerData - プレイヤーデータ
 * @param {Object[]} allCards - 全カードデータ
 * @param {string} packType - パックタイプ
 * @param {number} packCount - 開封パック数
 * @param {Map} [cardValueMap] - カードID -> 価値情報のマップ
 * @returns {Object} { success, error?, cards?, playerData?, packCount?, packType? }
 */
export const openMultipleTypedUnopenedPacks = (playerData, allCards, packType, packCount, cardValueMap = null) => {
  // 未開封パックの確認
  const counts = getTypedUnopenedPackCounts(playerData);
  const availablePacks = counts[packType] || 0;
  if (availablePacks < packCount) {
    return {
      success: false,
      error: `${getPackTypeName(packType)}が足りません（${availablePacks}個しかありません）`,
    };
  }

  // 価値マップがない場合は計算
  const valueMap = cardValueMap || valueCalculator.calculateAllCardValues(allCards);

  // 属性マッピング
  const attributeMap = {
    fire: '炎',
    water: '水',
    light: '光',
    dark: '闇',
    primitive: '原始',
    future: '未来',
  };

  // 全パックを開封してカードを統合
  const allOpenedCards = [];
  for (let i = 0; i < packCount; i++) {
    let packCards;
    if (packType === 'normal') {
      packCards = openPack(allCards, valueMap);
    } else if (packType === 'royal') {
      packCards = openRoyalPack(allCards, valueMap);
    } else {
      const attribute = attributeMap[packType];
      if (!attribute) {
        continue;
      }
      packCards = openAttributePack(allCards, valueMap, attribute);
    }
    allOpenedCards.push(...packCards);
  }

  // 各カードをコレクションに追加 & 未開封パック数を減らす
  let newData = {
    ...playerData,
    unopenedPacksByType: {
      ...playerData.unopenedPacksByType,
      [packType]: (playerData.unopenedPacksByType?.[packType] || 0) - packCount,
    },
    stats: {
      ...playerData.stats,
      packsOpened: (playerData.stats.packsOpened || 0) + packCount,
    },
  };

  // ノーマルパックの場合、互換性のためunopenedPacksも更新
  if (packType === 'normal') {
    newData.unopenedPacks = Math.max(0, (playerData.unopenedPacks || 0) - packCount);
  }

  for (const { cardId, rarity } of allOpenedCards) {
    newData = collectionManager.addCard(newData, cardId, rarity);
  }

  return {
    success: true,
    cards: allOpenedCards,
    playerData: newData,
    packCount,
    packType,
  };
};

/**
 * 報酬パック全体の総数を取得
 * @param {Object} playerData - プレイヤーデータ
 * @returns {number} 総数
 */
export const getTotalUnopenedPackCount = (playerData) => {
  const counts = getTypedUnopenedPackCounts(playerData);
  return Object.values(counts).reduce((sum, count) => sum + count, 0);
};

// ========================================
// エクスポート
// ========================================

export const packSystem = {
  openPack,
  buyAndOpenPack,
  openFreePack,
  openUnopenedPack,
  getPackInfo,
  // まとめ買い・まとめ開封
  buyAndOpenMultiplePacks,
  openMultipleUnopenedPacks,
  getBulkBuyOptions,
  // 属性別パック
  openAttributePack,
  buyAndOpenAttributePack,
  getAttributePackInfo,
  // ロイヤルパック
  openRoyalPack,
  buyAndOpenRoyalPack,
  getRoyalPackInfo,
  buyAndOpenMultipleRoyalPacks,
  getRoyalBulkBuyOptions,
  // タイプ別未開封パック
  getPackTypeName,
  getPackTypeIcon,
  getTypedUnopenedPackCounts,
  openTypedUnopenedPack,
  openMultipleTypedUnopenedPacks,
  getTotalUnopenedPackCount,
};

export default packSystem;
